import { hasAuthCookie } from '@quintoandar/wat-cookie';

import LocalRouteFallback from 'components/LocalRouteFallback';
import AgentLeadReferralContainer from 'containers/AgentLeadReferral/Loadable';
import VisitDeepLinkRedirect from 'containers/VisitDeepLinkRedirect';
import AgendaContainer from 'containers/Agenda/Loadable';
import AgentKeysContainer from 'containers/AgentKeys/Loadable';
import ClientsCrmContainer from 'containers/ClientsCrm/Loadable';
import ContactUs from 'containers/ContactUs';
import OffersPage from 'Offer/OffersPage/Page/Loadable';
import consentTermContainer from 'containers/AmbassadorAgent/ConsentTerm/Loadable';
import createReferenceRoutes from 'routes';
import ROUTES from 'routes/constants';
import createLoginRoutes from 'routes/login';
import { isLocalAuthRuntime } from 'routes/localAuth';
import { LOCAL_LOGIN_PATH } from 'routes/localPaths';

const localProtectedRoutes = [
  {
    path: ROUTES.HOME,
    name: 'v2-home',
    component: AgendaContainer,
  },
  {
    path: '/v2/home/agenda',
    name: 'v2-home-agenda',
    component: AgendaContainer,
  },
  {
    path: '/v2/home/clientes',
    name: 'v2-home-clients',
    component: ClientsCrmContainer,
  },
  {
    path: '/v2/home/chaves',
    name: 'v2-home-keys',
    component: AgentKeysContainer,
  },
  {
    path: '/v2/home/propostas',
    name: 'v2-home-offers',
    component: OffersPage,
  },
  {
    path: '/v2/home/propostas/:offerId',
    name: 'v2-home-offer-detail',
    component: OffersPage,
  },
  {
    path: '/v2/home/*',
    name: 'v2-home-fallback',
    component: AgendaContainer,
  },
  {
    path: ROUTES.AGENDA_VISIT_DETAIL_V2,
    name: 'details-v2',
    component: VisitDeepLinkRedirect,
  },
  {
    path: '/chaves/imovel/:houseId/devolucao',
    name: 'return-keys-path',
    component: AgentKeysContainer,
  },
  {
    path: ROUTES.INDICATE_PROPERTY_PATH,
    name: 'indicate-property-with-keys',
    component: AgentLeadReferralContainer,
  },
  {
    path: '/indica-ai',
    name: 'indica-ai',
    component: AgentLeadReferralContainer,
  },
  {
    path: '/campeoes/termo-consentimento',
    name: 'champions-consent-term',
    component: consentTermContainer,
  },
  {
    path: '/v2/visita/cancelar/:visitCode',
    name: 'visit-cancel-deep-link',
    component: VisitDeepLinkRedirect,
  },
  {
    path: '/v2/visita/finalizar/:visitCode/:result',
    name: 'visit-finish-deep-link',
    component: VisitDeepLinkRedirect,
  },
  {
    path: '/proposta/comprar/:houseId',
    name: 'sale-proposal-local-fallback',
    component: OffersPage,
  },
  {
    path: ROUTES.CONTACT_US_PATH,
    name: 'contact-us',
    component: ContactUs,
  },
  {
    path: '/imovel/:houseId',
    name: 'house-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/imovel/:houseId/comprar',
    name: 'house-sale-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/imovel/:houseId/alugar',
    name: 'house-rent-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/tqc/:referralCode',
    name: 'tqc-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/corretor/atendimento/:serviceId',
    name: 'agent-support-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/favoritos',
    name: 'favorites-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/meus-dados',
    name: 'profile-local-account',
    component: LocalRouteFallback,
  },
  {
    path: '/alugar/imovel',
    name: 'rent-house-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/comprar/imovel',
    name: 'buy-house-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/chats/choose-client',
    name: 'choose-client-chat-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/mensagens',
    name: 'messages-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/messages',
    name: 'messages-alias-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/chats/corretor-proprietario',
    name: 'owner-chat-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/chats/corretor/:agentId/proprietario/:ownerId/imovel/:houseId',
    name: 'owner-house-chat-local-fallback',
    component: LocalRouteFallback,
  },
  {
    path: '/chats/:chatGroupUuid',
    name: 'chat-thread-local-fallback',
    component: LocalRouteFallback,
  },
];

function localLoginRedirect() {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const redirect = encodeURIComponent(currentPath || ROUTES.BASE_PATH);
  window.location.replace(`${LOCAL_LOGIN_PATH}?redirect=${redirect}`);
}

function standaloneOnEnter(referenceOnEnter) {
  return async (nextState) => {
    if (isLocalAuthRuntime() && !hasAuthCookie()) {
      localLoginRedirect();
      return;
    }

    if (referenceOnEnter) {
      await referenceOnEnter(nextState);
    }
  };
}

function addLocalRoutesToProtectedRoute(route) {
  if (route.name !== 'onlyForLoggedUsers') {
    return route;
  }

  return {
    ...route,
    childRoutes: [...route.childRoutes, ...localProtectedRoutes],
    onEnter: standaloneOnEnter(route.onEnter),
  };
}

export default function createStandaloneRoutes(store, App) {
  return [
    ...createLoginRoutes(),
    {
      component: App,
      childRoutes: createReferenceRoutes(store).map(addLocalRoutesToProtectedRoute),
    },
  ];
}
